import { createContext, useContext, useEffect, useState } from "react";
import { collection, documentId, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "./firebase";
import { seedPackages, seedIndividualRates, seedVehicleTypes } from "./seed";

const CatalogContext = createContext(null);

export const CatalogProvider = ({ children }) => {
  const [packages, setPackages] = useState([]);
  const [individualRates, setIndividualRates] = useState([]);
  const [vehicleCategories, setVehicleCategories] = useState([]);

  // Runs on every app load (not just when an admin opens /admin) so a newly
  // added category/rate in code — e.g. the bike service — reaches Firestore
  // as soon as possible. Each seed function only adds docs that don't exist
  // yet, so this is safe to attempt repeatedly and never overwrites an
  // admin's price edits made directly in Firestore.
  useEffect(() => {
    seedPackages().catch((e) => console.warn("Seed packages failed:", e.message));
    seedIndividualRates().catch((e) => console.warn("Seed individual rates failed:", e.message));
    seedVehicleTypes().catch((e) => console.warn("Seed vehicle types failed:", e.message));
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "packages"), (snap) => {
      // Spread data first, then force the real doc ID last — a stray `id`
      // field inside a manually-edited document must never win over it.
      const list = snap.docs
        .map((d) => ({ ...d.data(), id: d.id }))
        .filter((p) => typeof p.priceHatchback === "number" && typeof p.priceSUV === "number");
      list.sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
      setPackages(list);
    }, () => setPackages([]));
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "individual_rates"), orderBy(documentId())),
      (snap) => {
        // Two valid shapes: the car matrix (interiorExterior/exteriorOnly)
        // and the bike flat-rate doc (washPolish/waxing). Anything matching
        // neither is an unrelated/malformed doc and gets dropped.
        const list = snap.docs
          .map((d) => ({ ...d.data(), id: d.id }))
          .filter((r) =>
            (typeof r.interiorExterior === "number" && typeof r.exteriorOnly === "number") ||
            (typeof r.washPolish === "number" && typeof r.waxing === "number")
          );
        setIndividualRates(list);
      },
      () => setIndividualRates([])
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "vehicleTypes"), (snap) => {
      // Only accept docs that actually look like this app's vehicle-category
      // shape (must have a usable priceKey) — otherwise an unrelated/legacy
      // collection with a different schema would silently break every price
      // lookup that depends on it.
      const list = snap.docs
        .map((d) => ({ ...d.data(), id: d.id }))
        .filter((c) => c.priceKey === "priceHatchback" || c.priceKey === "priceSUV");
      list.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
      setVehicleCategories(list);
    }, () => setVehicleCategories([]));
    return unsub;
  }, []);

  return (
    <CatalogContext.Provider value={{ packages, individualRates, vehicleCategories }}>
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => useContext(CatalogContext);
